// src/pages/Masters/MProject/MProjectActionBar/Export.jsx
import React, { useState } from "react";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Export = ({ fetchAllData, className = "" }) => {
  const [showExportMenu, setShowExportMenu] = useState(false);

  const handleExportCSV = async () => {
    try {
      const data = await fetchAllData();
      if (!data || data.length === 0) {
        alert("No data to export!");
        return;
      }

      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(","),
        ...data.map(row => headers.map(header => 
          JSON.stringify(row[header] || "").replace(/"/g, '""')
        ).join(","))
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `export_${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setShowExportMenu(false);
    } catch (error) {
      console.error("CSV Export failed:", error);
      alert("Failed to export CSV. Please try again.");
    }
  };

  const handleExportExcel = async () => {
    try {
      const data = await fetchAllData();
      if (!data || data.length === 0) {
        alert("No data to export!");
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
      XLSX.writeFile(workbook, `export_${new Date().toISOString()}.xlsx`);
      setShowExportMenu(false);
    } catch (error) {
      console.error("Excel Export failed:", error);
      alert("Failed to export Excel. Please try again.");
    }
  };

  const handleExportPDF = async () => {
    try {
      const data = await fetchAllData();
      if (!data || data.length === 0) {
        alert("No data to export!");
        return;
      }

      const doc = new jsPDF();
      const headers = Object.keys(data[0]);
      const rows = data.map(row => headers.map(header => row[header]));

      // Add title
      doc.setFontSize(18);
      doc.text('Project Master', 105, 15, null, null, 'center');
      
      // Add underline
      doc.setLineWidth(0.5);
      doc.line(14, 17, 200, 17);
      
      // Add timestamp
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toISOString()}`, 14, 25);

      // Add the table
      doc.autoTable({
        head: [headers],
        body: rows,
        startY: 30,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [40, 84, 147] }
      });

      // Add footer with decreased opacity
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setTextColor(150); // Set gray color for opacity effect
        doc.text('© Indent Management Confident', 105, doc.internal.pageSize.height - 10, null, null, 'center');
      }

      doc.save(`export_${new Date().toISOString()}.pdf`);
      setShowExportMenu(false);
    } catch (error) {
      console.error("PDF Export failed:", error);
      alert("Failed to export PDF. Please try again.");
    }
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setShowExportMenu(!showExportMenu)}
        className={`px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors duration-200 ${className}`}
        aria-label="Export options"
        aria-expanded={showExportMenu}
      >
        Export
      </button>

      {showExportMenu && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-25 z-40"
            onClick={() => setShowExportMenu(false)}
          />
          
          {/* Export Menu */}
          <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 animate-slideIn">
            <div className="py-1" role="menu" aria-orientation="vertical">
              {/* PDF Export Option */}
              <button
                onClick={handleExportPDF}
                className="group flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150"
                role="menuitem"
              >
                <span className="mr-3 text-lg">📄</span>
                Export as PDF
              </button>

              {/* CSV Export Option */}
              <button
                onClick={handleExportCSV}
                className="group flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150"
                role="menuitem"
              >
                <span className="mr-3 text-lg">📊</span>
                Export as CSV
              </button>

              {/* Excel Export Option */}
              <button
                onClick={handleExportExcel}
                className="group flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150"
                role="menuitem"
              >
                <span className="mr-3 text-lg">📑</span>
                Export as Excel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Export;